import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Link, Typography } from "@mui/material";
import { DASHBOARD_PATH } from "../../utils/constants/routes";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  BASE,
  BORDER,
  BRAND,
  GREY,
  PRIMARY,
} from "../../utils/constants/colors";

const styles = {
  // ── Page ──────────────────────────────────────────────────────────────────
  root: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: { xs: "20px", sm: "32px" },
    backgroundColor: BASE.BACKGROUND,
    position: "relative",
    overflow: "hidden",
  },

  glowTopRight: {
    position: "absolute",
    top: "-160px",
    right: "-140px",
    width: "520px",
    height: "520px",
    borderRadius: "50%",
    backgroundColor: PRIMARY.W_50,
    opacity: 0.3,
    pointerEvents: "none",
  },
  glowBottomLeft: {
    position: "absolute",
    bottom: "-160px",
    left: "-120px",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    backgroundColor: "rgba(32,128,246,0.05)",
    pointerEvents: "none",
  },

  // ── Card ──────────────────────────────────────────────────────────────────
  card: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: "900px",
    backgroundColor: BASE.WHITE,
    borderRadius: "28px",
    border: `1px solid ${BORDER.DEFAULT}`,
    boxShadow: `
      0 2px 4px rgba(30,41,57,0.04),
      0 12px 32px rgba(30,41,57,0.08),
      0 40px 80px rgba(30,41,57,0.06)
    `,
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
    overflow: "hidden",
  },

  // ── Left Panel ────────────────────────────────────────────────────────────
  panel: {
    backgroundColor: BRAND.PRIMARY,
    borderRight: { xs: "none", md: `1px solid ${PRIMARY.W_600}` },
    borderBottom: { xs: `1px solid ${PRIMARY.W_600}`, md: "none" },
    padding: { xs: "32px 28px 28px", md: "52px 28px" },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  panelGlowBottom: {
    position: "absolute",
    bottom: "-80px",
    left: "-80px",
    width: "240px",
    height: "240px",
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.1)",
    pointerEvents: "none",
  },
  panelGlowTop: {
    position: "absolute",
    top: "-60px",
    right: "-60px",
    width: "200px",
    height: "200px",
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.07)",
    pointerEvents: "none",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 12px",
    borderRadius: "999px",
    backgroundColor: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "rgba(255,255,255,0.88)",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    mb: "24px",
    zIndex: 1,
  },

  iconWrap: {
    width: "90px",
    height: "90px",
    borderRadius: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.24)",
    boxShadow: `0 12px 32px rgba(0,0,0,0.15)`,
    mb: "24px",
    zIndex: 1,
    transition: "transform 300ms ease",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },

  panelTitle: {
    color: BASE.WHITE,
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    mb: "8px",
    zIndex: 1,
  },
  panelBody: {
    color: "rgba(255,255,255,0.65)",
    fontSize: "0.83rem",
    lineHeight: 1.7,
    maxWidth: "170px",
    zIndex: 1,
  },

  // ── Right Content ─────────────────────────────────────────────────────────
  content: {
    padding: { xs: "32px 24px 36px", md: "56px 60px" },
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "5px 12px",
    borderRadius: "999px",
    backgroundColor: PRIMARY.W_50,
    border: `1px solid ${PRIMARY.W_200}`,
    color: PRIMARY.W_700,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    mb: "20px",
  },
  statusDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: BRAND.PRIMARY,
    boxShadow: `0 0 0 3px ${PRIMARY.W_100}`,
    flexShrink: 0,
  },

  title: {
    color: GREY.PRIMARY,
    fontWeight: 800,
    fontSize: { xs: "2.2rem", sm: "2.8rem" },
    letterSpacing: "-0.04em",
    lineHeight: 1.05,
    mb: "6px",
  },
  titleAccent: {
    color: BRAND.PRIMARY,
  },

  divider: {
    width: "44px",
    height: "3px",
    borderRadius: "2px",
    backgroundColor: BRAND.PRIMARY,
    mt: "6px",
    mb: "20px",
  },

  description: {
    color: GREY.SECONDARY,
    fontSize: "1rem",
    lineHeight: 1.75,
    maxWidth: "420px",
    mb: "28px",
  },

  infoBox: {
    width: "100%",
    maxWidth: "420px",
    padding: "16px 20px 16px 24px",
    borderRadius: "16px",
    border: `1px solid ${BORDER.DEFAULT}`,
    backgroundColor: BASE.BACKGROUND_LIGHT,
    mb: "32px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "3px",
      height: "100%",
      backgroundColor: BRAND.PRIMARY,
      borderRadius: "3px 0 0 3px",
    },
  },
  infoLabel: {
    color: GREY.TERTIARY,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    mb: "6px",
  },
  infoText: {
    color: GREY.SECONDARY,
    fontSize: "0.9rem",
    lineHeight: 1.7,
  },
  infoLink: {
    color: BRAND.PRIMARY,
    fontWeight: 600,
    textDecoration: "none",
    transition: "color 200ms ease",
    "&:hover": {
      color: PRIMARY.W_600,
      textDecoration: "underline",
      textUnderlineOffset: "3px",
    },
  },

  button: {
    minWidth: { xs: "100%", sm: "auto" },
    padding: "14px 28px",
    borderRadius: "14px",
    backgroundColor: BRAND.PRIMARY,
    color: BASE.WHITE,
    fontWeight: 700,
    textTransform: "none",
    fontSize: "0.95rem",
    letterSpacing: "-0.01em",
    boxShadow: `0 8px 24px ${PRIMARY.W_500}33`,
    border: `1px solid ${PRIMARY.W_600}`,
    transition: "all 240ms cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: PRIMARY.W_400,
      boxShadow: `0 14px 32px ${PRIMARY.W_500}44`,
      transform: "translateY(-2px)",
    },
    "&:active": {
      transform: "translateY(0px)",
      boxShadow: `0 4px 12px ${PRIMARY.W_500}33`,
    },
  },
};

const AccessDenied = () => {
  const navigate = useNavigate();

  return (
    <Box sx={styles.root}>
      <Box sx={styles.glowTopRight} />
      <Box sx={styles.glowBottomLeft} />

      <Box sx={styles.card}>
        {/* ── Left Panel ── */}
        <Box sx={styles.panel}>
          <Box sx={styles.panelGlowBottom} />
          <Box sx={styles.panelGlowTop} />

          <Box sx={styles.badge}>SDR Access Only</Box>

          <Box sx={styles.iconWrap}>
            <LockOutlinedIcon sx={{ fontSize: 40, color: BASE.WHITE }} />
          </Box>

          <Typography sx={styles.panelTitle}>Access Control</Typography>
          <Typography sx={styles.panelBody}>
            Limited to SDR users with required workspace permissions.
          </Typography>
        </Box>

        {/* ── Right Content ── */}
        <Box sx={styles.content}>
          <Box sx={styles.statusPill}>
            <Box sx={styles.statusDot} />
            403 Restricted
          </Box>

          <Typography variant="h2" sx={styles.title}>
            Access{" "}
            <Box component="span" sx={styles.titleAccent}>
              Restricted
            </Box>
          </Typography>

          <Box sx={styles.divider} />

          <Typography sx={styles.description}>
            You don't have permission to view this page. This section is
            exclusively available to users with SDR workspace roles.
          </Typography>

          <Box sx={styles.infoBox}>
            <Typography sx={styles.infoLabel}>Need Access?</Typography>
            <Typography sx={styles.infoText}>
              Contact your Account Manager.
            </Typography>
          </Box>

          <Button
            sx={styles.button}
            onClick={() => navigate(DASHBOARD_PATH)}
            variant="contained"
            disableElevation
            endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
          >
            Return to Dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AccessDenied;
